import React, { useContext, useState } from 'react';
import { 
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Badge
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PeopleIcon from '@mui/icons-material/People';
import AuthContext from '../contexts/AuthContext';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export default function Profile() {
  const { user, authData, managerRoles, tokens } = useContext(AuthContext);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  // Invitation action state
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  // Count of pending invitations
  const invCount = authData?.invitations?.length || 0;
  // Handlers for invitation actions
  const handleActionClick = (inv, type) => {
    setSelectedInvitation(inv);
    setActionType(type);
    setActionDialogOpen(true);
  };
  const handleActionConfirm = async () => {
    if (!selectedInvitation) return;
    setActionLoading(true);
    let success = false;
    try {
      const res = await fetch(
        `${apiBase}/users/${user.id}/invitation`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.id_token}`
          },
          body: JSON.stringify({ id: selectedInvitation.id, status: actionType })
        }
      );
      if (!res.ok) throw new Error(`Failed to ${actionType.toLowerCase()} invitation`);
      success = true;
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
      setActionDialogOpen(false);
      setSelectedInvitation(null);
      setActionType('');
      if (success) {
        window.location.reload();
      }
    }
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Profile
      </Typography>
      {(process.env.NODE_ENV !== 'production' || process.env.REACT_APP_STAGE === 'dev') && tokens?.id_token && (
        <Box my={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={() => navigator.clipboard.writeText(tokens.id_token)}
          >
            Copy ID Token
          </Button>
        </Box>
      )}
      {/* User Information and Professional Profile side by side */}
      <Box display="flex" my={2} gap={2} flexWrap="wrap">
        {user && (
          <Card sx={{ flex: 1, minWidth: 300 }}>
            <CardHeader title="User Information" />
            <CardContent>
              <Grid container spacing={2}>
                {/* ID with copy button */}
                {'id' in user && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">ID</Typography>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1" sx={{ mr: 1 }}>{user.id}</Typography>
                      <Tooltip title="Copy ID">
                        <IconButton size="small" onClick={() => navigator.clipboard.writeText(user.id)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                )}
                {Object.entries(user)
                  .filter(([key]) => !['id', 'created_at', 'updated_at'].includes(key))
                  .map(([key, value]) => {
                    const base = key.replace(/_/g, ' ').trim();
                    const label = base ? base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() : '';
                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="subtitle2">{label}</Typography>
                        <Typography variant="body1">{String(value)}</Typography>
                      </Grid>
                    );
                  })}
              </Grid>
            </CardContent>
          </Card>
        )}
        {authData?.professional_record && (
          <Card sx={{ flex: 1, minWidth: 300 }}>
            <CardHeader title="Professional Profile" />
            <CardContent>
              <Grid container spacing={2}>
                {Object.entries(authData.professional_record)
                  .filter(([key]) => !['id', 'user_id', 'created_at', 'updated_at'].includes(key))
                  .map(([key, value]) => {
                    const base = key.replace(/_/g, ' ').trim();
                    const label = base ? base.charAt(0).toUpperCase() + base.slice(1).toLowerCase() : '';
                    return (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="subtitle2">{label}</Typography>
                        <Typography variant="body1">{String(value)}</Typography>
                      </Grid>
                    );
                  })}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>
      {/* Invitations Section */}
      {authData?.invitations?.length > 0 && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Badge
              badgeContent={invCount}
              color="error"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              overlap="circular"
              sx={{
                mr: 1,
                '& .MuiBadge-badge': {
                  minWidth: '16px',
                  height: '16px',
                  fontSize: '0.65rem',
                }
              }}
            >
              <MailOutlineIcon fontSize="small" />
            </Badge>
            <Typography variant="h6">Invitations</Typography>
          </Box>
        <Table>
          <TableHead>
                <TableRow>
                  <TableCell>Organization</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {authData.invitations.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell>{inv.organization_name || inv.organization_id}</TableCell>
                    <TableCell>{inv.role}</TableCell>
                    <TableCell>{inv.status}</TableCell>
                    <TableCell align="right">
                      {inv.status === 'Pending' && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleActionClick(inv, 'Accepted')}
                            sx={{ mr: 1 }}
                          >
                            Accept
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleActionClick(inv, 'Declined')}
                          >
                            Decline
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </>
      )}
      {/* Invitation Action Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => !actionLoading && setActionDialogOpen(false)}
      >
        <DialogTitle>{actionType} Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {actionType.toLowerCase()} the invitation to <strong>{selectedInvitation?.organization_name}</strong> as <strong>{selectedInvitation?.role}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleActionConfirm} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Roles at Organizations Table (Personnel users only) */}
      {user?.role === 'Personnel' && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <PeopleIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Roles at Organizations</Typography>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Organization Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {authData?.roles_at_organizations?.map((entry) => {
                const org = authData.organizations?.find(o => o.id === entry.organization_id);
                const orgName = org?.name || entry.organization_id;
                return (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.role}</TableCell>
                    <TableCell>{orgName}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
      {managerRoles && managerRoles.length > 0 && (
        <>
          <Divider />
          <Box my={2}>
            <Typography variant="h6">Manager Roles</Typography>
            <List>
              {managerRoles.map((role, index) => (
                <ListItem key={index}>
                  <ListItemText primary={String(role)} />
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}
    </Container>
  );
}
